import { useEffect, useMemo, useRef, useCallback, useState } from 'react'
import { useStore } from '@/store'
import type { TimeWindow } from '@/types'
import { aggWindowForTick, parseAcePlayerTimestamp } from '@/analysis/tickTimestamp'

const TICK_COUNT = 6

const WINDOWS: { label: string; value: TimeWindow }[] = [
  { label: '1 hr', value: '1h' },
  { label: '12 hrs', value: '12h' },
  { label: '24 hrs', value: '24h' },
  { label: 'All', value: 'all' },
]

function toDatetimeLocalValue(ms: number): string {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function fromDatetimeLocalValue(v: string): number | null {
  const t = new Date(v).getTime()
  return Number.isFinite(t) ? t : null
}

export default function Trackbar() {
  const timeMode     = useStore(s => s.timeMode)
  const timeWindow   = useStore(s => s.window)
  const incidentSync = useStore(s => s.incidentSync)
  const incidents    = useStore(s => s.incidents)
  const setTimeMode  = useStore(s => s.setTimeMode)
  const setWindow    = useStore(s => s.setWindow)
  const setHistoricTs = useStore(s => s.setHistoricTs)
  const setHistoricRange = useStore(s => s.setHistoricRange)
  const clearSync    = useStore(s => s.clearIncidentSync)
  const syncToInc    = useStore(s => s.syncToIncident)
  const historicTs   = useStore(s => s.historicTs)
  const historicFromTs = useStore(s => s.historicFromTs)
  const nowTs        = useStore(s => s.nowTs)
  const view         = useStore(s => s.view)
  const drillNode    = useStore(s => s.drillNode)
  const searchResult = useStore(s => s.searchResult)
  const searchResultDrill = useStore(s => s.searchResultDrill)
  const worldFlowResult = useStore(s => s.worldFlowResult)

  const isLive = timeMode === 'live'
  const isCustom = timeWindow === 'custom'

  /**
   * Active display window for the dataset on screen.
   * Anchored to last data receipt (WS/REST) so the readout refreshes every tick;
   * span follows the Trackbar time filter (and custom from/to when set).
   */
  const activeWindow = useMemo(() => {
    if (isCustom && historicFromTs != null && historicTs != null) {
      return {
        fromMs: Math.min(historicFromTs, historicTs),
        toMs: Math.max(historicFromTs, historicTs),
      }
    }

    const envelope =
      view === 'world'
        ? worldFlowResult
        : view === 'hex' && drillNode && searchResultDrill
          ? searchResultDrill
          : searchResult

    const spanMs =
      timeWindow === '1h' ? 3_600_000
        : timeWindow === '12h' ? 43_200_000
          : timeWindow === '24h' ? 86_400_000
            : timeWindow === 'all' ? null
              : 3_600_000

    // Live window advances only when the current view's envelope updates.
    const dataTo = isLive
      ? (typeof envelope?.toMs === 'number' && Number.isFinite(envelope.toMs) ? envelope.toMs : 0)
      : (typeof historicTs === 'number' && Number.isFinite(historicTs) ? historicTs : 0)

    if (!dataTo) return null

    if (spanMs == null) {
      const from =
        typeof envelope?.fromMs === 'number' && Number.isFinite(envelope.fromMs)
          ? envelope.fromMs
          : 0
      return { fromMs: from, toMs: dataTo }
    }

    const toMs = isLive ? dataTo : (historicTs ?? dataTo)
    return { fromMs: toMs - spanMs, toMs }
  }, [
    isCustom,
    historicFromTs,
    historicTs,
    view,
    drillNode,
    searchResult,
    searchResultDrill,
    worldFlowResult,
    timeWindow,
    isLive,
    nowTs,
  ])

  const [draftFrom, setDraftFrom] = useState('')
  const [draftTo, setDraftTo] = useState('')

  useEffect(() => {
    if (!isCustom) return
    const to = historicTs ?? nowTs
    const from = historicFromTs ?? to - 10_000
    setDraftFrom(toDatetimeLocalValue(from))
    setDraftTo(toDatetimeLocalValue(to))
  }, [isCustom, historicTs, historicFromTs, nowTs])

  const windowMs = (w: TimeWindow) => {
    if (w === '1h') return 3_600_000
    if (w === '12h') return 43_200_000
    if (w === '24h') return 86_400_000
    if (w === 'custom' && historicFromTs != null && historicTs != null) {
      return Math.max(10_000, historicTs - historicFromTs)
    }
    return 3_600_000
  }

  const fmtLabel = useCallback((ts: number, spanMs: number) => {
    const d = new Date(ts)
    if (spanMs > 3_600_000) {
      return d.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    }
    return d.toLocaleTimeString('en-GB')
  }, [])

  const tickLabels = useMemo(() => {
    const span = windowMs(timeWindow)
    const anchor = isLive
      ? (activeWindow?.toMs ?? nowTs)
      : (historicTs ?? nowTs)
    const labels: string[] = []
    for (let i = 0; i < TICK_COUNT; i++) {
      if (i === TICK_COUNT - 1) {
        labels.push(isCustom ? 'end' : 'now')
      } else {
        const t = anchor - span + (span * i) / (TICK_COUNT - 1)
        labels.push(fmtLabel(t, span))
      }
    }
    return labels
  }, [timeWindow, isLive, nowTs, historicTs, historicFromTs, isCustom, fmtLabel, activeWindow])

  // Live clock mirrors last tick so the left timestamp stays in sync with the window.
  const liveClockTs = isLive
    ? (activeWindow?.toMs ?? nowTs)
    : null

  const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n))

  const pctToTs = (pct: number) => {
    if (pct >= 100) return null
    const span = windowMs(timeWindow)
    const back = (1 - pct / 100) * span
    return Math.round(nowTs - back)
  }

  const tsToPct = (ts: number | null) => {
    if (ts == null || isLive) return 100
    const span = windowMs(timeWindow)
    const back = clamp(nowTs - ts, 0, span)
    return clamp(Math.round((1 - back / span) * 100), 0, 100)
  }

  function formatTime(ts: number) {
    return new Date(ts).toLocaleTimeString('en-GB')
  }

  const debRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setHistoricTsDebounced = useMemo(() => {
    return (ts: number | null) => {
      if (debRef.current) globalThis.clearTimeout(debRef.current)
      debRef.current = globalThis.setTimeout(() => {
        setHistoricTs(ts)
      }, 180)
    }
  }, [setHistoricTs])

  useEffect(() => () => { if (debRef.current) globalThis.clearTimeout(debRef.current) }, [])

  const applyCustomRange = () => {
    // Prefer AcePlayer-style numeric paste in From if present.
    const tickFrom = parseAcePlayerTimestamp(draftFrom)
    if (tickFrom != null && !draftTo) {
      const { fromMs, toMs } = aggWindowForTick(tickFrom)
      setDraftFrom(toDatetimeLocalValue(fromMs))
      setDraftTo(toDatetimeLocalValue(toMs))
      setHistoricRange(fromMs, toMs)
      return
    }
    const from = fromDatetimeLocalValue(draftFrom) ?? tickFrom
    const to = fromDatetimeLocalValue(draftTo)
    if (from == null || to == null) return
    setHistoricRange(from, to)
  }

  const snapToTenSeconds = () => {
    const tick = parseAcePlayerTimestamp(draftTo) ?? parseAcePlayerTimestamp(draftFrom)
    const to = tick ?? fromDatetimeLocalValue(draftTo) ?? historicTs ?? nowTs
    const { fromMs, toMs } = aggWindowForTick(to)
    setDraftFrom(toDatetimeLocalValue(fromMs))
    setDraftTo(toDatetimeLocalValue(toMs))
    setHistoricRange(fromMs, toMs)
  }

  return (
    <div style={{
      background: 'var(--bg-1)', borderTop: '0.5px solid var(--bd-0)',
      padding: '9px 16px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6,
      backdropFilter: 'blur(12px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2 }}>
          {(['live', 'historic'] as const).map(m => (
            <span
              key={m}
              className={`chip${timeMode === m ? ' on' : ''}`}
              onClick={() => setTimeMode(m)}
            >{m.charAt(0).toUpperCase() + m.slice(1)}</span>
          ))}
        </div>

        <div className="vd" />

        <div style={{ display: 'flex', gap: 3 }}>
          {WINDOWS.map(w => (
            <span
              key={w.value}
              className={`chip${timeWindow === w.value ? ' on' : ''}`}
              onClick={() => setWindow(w.value)}
              style={{ fontSize: 10 }}
            >{w.label}</span>
          ))}
          <span
            className={`chip${isCustom ? ' on' : ''}`}
            style={{ fontSize: 10 }}
            onClick={() => setWindow('custom')}
            title="Exact from/to or AcePlayer tick ts → 10s agg window"
          >Tick / Custom</span>
        </div>

        <div className="vd" />

        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--t-1)', minWidth: 90 }} id="track-time">
          {isLive
            ? formatTime(liveClockTs ?? nowTs)
            : isCustom && historicFromTs != null && historicTs != null
              ? `${formatTime(historicFromTs)}–${formatTime(historicTs)}`
              : historicTs ? formatTime(historicTs) : 'historic'}
        </span>

        {activeWindow && (
          <span
            title="Active aggregation window for the data on screen"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: incidentSync ? 'var(--crit)' : 'var(--t-2)',
              padding: incidentSync ? '2px 8px' : undefined,
              borderRadius: incidentSync ? 20 : undefined,
              background: incidentSync ? 'var(--crit-bg)' : undefined,
              border: incidentSync ? '0.5px solid var(--crit-bd)' : undefined,
              whiteSpace: 'nowrap',
            }}
          >
            {formatTime(activeWindow.fromMs)} → {formatTime(activeWindow.toMs)}
          </span>
        )}

        <div style={{ flex: 1 }} />

        {incidentSync && (
          <div style={{
            fontSize: 10, padding: '3px 10px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5,
            background: 'var(--crit-bg)', color: 'var(--crit)', border: '0.5px solid var(--crit-bd)',
          }}>
            Synced · {incidentSync.ts}
            <span style={{ cursor: 'pointer', opacity: .6 }} onClick={clearSync}>✕</span>
          </div>
        )}
      </div>


      {isCustom && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 10, color: 'var(--t-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
            From
            <input
              type="datetime-local"
              step={1}
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              style={{
                height: 26, fontSize: 11, fontFamily: 'var(--font-mono)',
                background: 'var(--bg-3)', color: 'var(--t-0)',
                border: '0.5px solid var(--bd-1)', borderRadius: 6, padding: '0 8px',
              }}
            />
          </label>
          <label style={{ fontSize: 10, color: 'var(--t-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
            To
            <input
              type="datetime-local"
              step={1}
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              style={{
                height: 26, fontSize: 11, fontFamily: 'var(--font-mono)',
                background: 'var(--bg-3)', color: 'var(--t-0)',
                border: '0.5px solid var(--bd-1)', borderRadius: 6, padding: '0 8px',
              }}
            />
          </label>
          <button
            type="button"
            className="chip on"
            style={{ fontSize: 10, cursor: 'pointer', border: 'none' }}
            onClick={applyCustomRange}
          >Apply range</button>
          <button
            type="button"
            className="chip"
            style={{ fontSize: 10, cursor: 'pointer' }}
            onClick={snapToTenSeconds}
            title="Snap to floor(ts/10s)*10s aggregation window"
          >Snap 10s window</button>
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: -8, left: 0, right: 0, height: 8, pointerEvents: 'none' }}>
          {incidents.filter(i => i.status === 'open').map((inc, idx) => {
            const pct = [52, 37, 22][idx] ?? 50
            return (
              <div
                key={inc.id}
                title={`${inc.sid} · ${inc.metric}=${inc.val}`}
                onClick={() => syncToInc(inc.id, inc.openedAt, inc.sid)}
                style={{
                  position: 'absolute', left: `${pct}%`, width: 2, height: 14,
                  background: 'var(--crit)', opacity: .75, borderRadius: 1,
                  cursor: 'pointer', pointerEvents: 'all', top: 0,
                }}
              />
            )
          })}
        </div>

        <input
          type="range" min={0} max={100} value={tsToPct(historicTs)}
          style={{ width: '100%', accentColor: 'var(--info)' }}
          disabled={isCustom}
          onChange={e => {
            const pct = Number(e.target.value)
            const ts = pctToTs(pct)
            if (ts == null) {
              setTimeMode('live')
              setHistoricTsDebounced(null)
              return
            }
            setTimeMode('historic')
            setHistoricTsDebounced(ts)
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 2px', marginTop: 2 }}>
          {tickLabels.map((t, i) => (
            <span key={i} style={{ fontSize: 9, color: 'var(--t-2)', fontFamily: 'var(--font-mono)' }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  )
}
